import React, { useEffect, useRef, useState } from 'react';
import { SimpleReveal } from './SimpleReveal';
import { Button } from './ui/button';
import { X, Check, Lightbulb, ArrowRight } from 'lucide-react';
import { SpotifyPhoneMockup } from './SpotifyPhoneMockup';
interface WelcomeSectionProps {
  applyUrl: string;
}
// Video Introduction Section (no background, meant to be wrapped in DarkChapter)
export function WelcomeVideoSection({
  applyUrl
}: WelcomeSectionProps) {
  return <section className="relative py-6 lg:py-32 overflow-hidden">
      {/* Blue Horizontal Wave - Full Width Background */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Main horizontal flowing gradient */}
        <div className="absolute top-[30%] left-0 right-0 h-[500px] opacity-30" style={{
        background: 'linear-gradient(90deg, transparent 0%, rgba(0,113,227,0.6) 25%, rgba(0,191,255,0.7) 50%, rgba(0,113,227,0.6) 75%, transparent 100%)',
        filter: 'blur(80px)'
      }} />
        {/* Secondary softer layer for depth */}
        <div className="absolute top-[35%] left-0 right-0 h-[400px] opacity-20" style={{
        background: 'linear-gradient(90deg, transparent 10%, rgba(0,191,255,0.5) 40%, rgba(0,191,255,0.6) 60%, transparent 90%)',
        filter: 'blur(60px)'
      }} />
      </div>

      {/* Content on top of gradient */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          {/* Main Heading - Hero Size - Desktop only */}
          <div className="hidden lg:block max-w-3xl mx-auto mb-10">
            <SimpleReveal delay={0}>
              <h2 className="headline-xl text-center mb-2 leading-[0.95]">
                <span className="block text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-1">Welcome To The</span>
                <span className="block font-black text-white text-5xl lg:text-7xl xl:text-8xl">
                  Artist Engine
                </span>
              </h2>
            </SimpleReveal>

            {/* Subheading - One-Two Punch */}
            <SimpleReveal delay={160}>
              <p className="body-large text-white/60 text-center mb-5 max-w-2xl mx-auto">
                The problem isn't your music. It's the system around it.
              </p>
            </SimpleReveal>
          </div>

          {/* Video Section */}
          <div className="max-w-4xl mx-auto relative">
            {/* Video Container */}
            <SimpleReveal delay={240}>
              <div className="relative rounded-2xl overflow-hidden shadow-[0_20px_50px_-15px_rgba(0,0,0,0.3)] transition-all duration-500 hover:shadow-[0_30px_70px_-10px_rgba(0,0,0,0.6)] hover:-translate-y-2 hover:scale-[1.01] group">

                {/* Video iframe */}
                <div style={{
              padding: '56.25% 0 0 0',
              position: 'relative'
            }}>
                  <iframe src="https://player.vimeo.com/video/1152297136?badge=0&autopause=0&player_id=0&app_id=58479" frameBorder="0" allow="autoplay; fullscreen; picture-in-picture; clipboard-write" style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%'
              }} title="DK Artists" aria-label="DK Artists introduction video" />
                </div>
              </div>
            </SimpleReveal>

            {/* CTA Button */}
            <SimpleReveal delay={320}>
              <div className="flex flex-col items-center mt-12">
                <div className="inline-block group cta-pulse md:rounded-xl">
                  <Button variant="hero-black-ring" size="hero" className="w-auto relative border border-[#0071e3]/40 shadow-[0_0_14px_rgba(0,113,227,0.18)] hover:border-[#0071e3]/60 hover:shadow-[0_0_24px_rgba(0,113,227,0.35)] transition-all duration-300 hover:scale-[1.02]" asChild>
                    <a href={applyUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2">
                      Apply Now
                      <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
                    </a>
                  </Button>
                </div>
                
                <p className="text-xs sm:text-sm text-white/60 mt-3">
                  We review all applications personally
                </p>
              </div>
            </SimpleReveal>
          </div>
      </div>
    </section>;
}

// The Problem Section (white background)
export function RealIssueSection() {
  return <section id="the-real-issue" className="bg-white py-16 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-[60fr_40fr] gap-8 lg:gap-16 items-start">
            {/* Left Column - Text Content (Scrolls naturally) */}
            <div>
              {/* Badge */}
              <SimpleReveal delay={0}>
                <div className="inline-flex items-center gap-2 px-2 py-1 md:px-3 md:py-1.5 bg-[#0071e3]/10 border border-[#0071e3]/25 rounded-md mb-4 lg:mb-6 transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] hover:bg-[#0071e3]/15 hover:border-[#0071e3]/35 hover:shadow-[0_0_20px_rgba(0,113,227,0.3)]">
                  <span className="font-mono text-xs font-medium text-[#0071e3] tracking-wider md:tracking-[0.15em] uppercase">
                    &gt; The Real Issue
                  </span>
                </div>
              </SimpleReveal>

              {/* Headline */}
              <SimpleReveal delay={80}>
                <div className="mb-8 lg:mb-10">
                  <h3 className="headline-lg text-black">You're just one song away. But this is{' '}
                    <span className="inline-block bg-[#0071e3] text-white px-3 py-1 rounded-lg font-bold -rotate-2 shadow-[0_0_40px_rgba(0,113,227,0.7)]">
                      the trap
                    </span>
                    {' '}that most artists fall into...</h3>
                </div>
              </SimpleReveal>

              {/* Paragraphs 1-2 */}
              <SimpleReveal delay={160}>
                <div className="space-y-6 mb-12 lg:mb-10">
                  <p className="body-large text-black/85 max-w-[60ch]">
                    They focus on <strong>views instead of fans</strong>. They build <strong>presence instead of momentum</strong>. They release songs and create content, but <strong>nothing compounds</strong>.
                  </p>
                  <p className="body-large text-black/85 max-w-[60ch]">
                    They think the answer is more. More posts, more effort, more noise. But <strong>more isn't growth. It's exhaustion</strong>.
                  </p>
                </div>
              </SimpleReveal>

              {/* Paragraph 3 + Problem Bullets */}
              <SimpleReveal delay={240}>
                <div className="mb-10">
                  <p className="body-large text-black/85 max-w-[60ch] mb-6">
                    Here's what that usually looks like:
                  </p>
                  <div className="space-y-4">
                    {["You post more content, hoping something will finally catch on.", "You release songs and see a spike that fades a week later.", "You try to do everything yourself and end up overwhelmed.", "You feel busy every day but never feel like you're moving forward."].map((text, index) => <SimpleReveal key={index} delay={320 + index * 60}>
                        <div className="group flex items-start gap-4 bg-white/3 rounded-lg p-4 shadow-xl shadow-black/5 transition-all duration-[600ms] ease-[cubic-bezier(0.16,1,0.3,1)] hover:bg-white hover:shadow-2xl hover:shadow-black/10 hover:translate-x-2">
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100/50 border border-gray-200 flex items-center justify-center group-hover:bg-gray-200/50 transition-all duration-[600ms] ease-[cubic-bezier(0.16,1,0.3,1)]">
                            <X className="w-5 h-5 text-gray-400" />
                          </div>
                          <p className="body-large text-black/85 max-w-[55ch]">
                            {text}
                          </p>
                        </div>
                      </SimpleReveal>)}
                  </div>
                </div>
              </SimpleReveal>

              {/* Moment of Realization Callout */}
              <SimpleReveal delay={560}>
                <div className="my-10 p-6 bg-white/3 rounded-lg shadow-xl shadow-black/5 shadow-[0_0_0_1px_rgba(0,113,227,0.08)] transition-all duration-300 hover:bg-white hover:shadow-2xl hover:shadow-black/10 hover:shadow-[0_0_0_1px_rgba(0,113,227,0.15),0_0_20px_rgba(0,113,227,0.1)] hover:translate-x-2">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-50/50 flex items-center justify-center">
                      <Lightbulb className="w-6 h-6 text-blue-600/70" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-black mb-3">
                        Here's the realization
                      </h4>
                      <p className="body-large text-black/90 max-w-[55ch]">
                        This isn't a creativity problem. It's a <strong>structure problem</strong>. Without the right system, even great music stays invisible.
                      </p>
                    </div>
                  </div>
                </div>
              </SimpleReveal>

              {/* Paragraph 4 */}
              <SimpleReveal delay={640}>
                <div className="mb-8">
                  <p className="body-large text-black/85 max-w-[60ch]">
                    That cycle isn't about talent. It's about direction. Most artists are working hard inside a <strong>broken system</strong> that rewards effort but never builds momentum.
                  </p>
                </div>
              </SimpleReveal>

              {/* Paragraph 5 - Solution */}
              <SimpleReveal delay={720}>
                <div className="mb-8">
                  <p className="body-large text-black/85 max-w-[60ch]">
                    Real growth doesn't come from doing more. It comes from having a <strong>structure that connects everything</strong> you already do - your music, your content, and your marketing - so they work together instead of apart.
                  </p>
                </div>
              </SimpleReveal>

              {/* Paragraphs 6-7 */}
              <SimpleReveal delay={800}>
                <div className="space-y-6">
                  <p className="body-large text-black/85 max-w-[60ch]">
                    That's what we build at DK Artists. A <strong>done-for-you framework</strong> that removes the guesswork and turns creativity into <strong>measurable, lasting growth</strong>.
                  </p>
                  <p className="body-large text-black/85 max-w-[60ch]">
                    You focus on creating. We'll help you grow it.
                  </p>
                </div>
              </SimpleReveal>
            </div>

            {/* Right Column - iPhone Mockup (Sticky) */}
            <div className="hidden lg:flex lg:items-center lg:justify-end lg:sticky lg:top-24 relative z-10">
              <SimpleReveal delay={200}>
                <SpotifyPhoneMockup />
              </SimpleReveal>
            </div>
          </div>
      </div>
    </section>;
}

// Default export that composes both sections
export default function WelcomeSection({
  applyUrl
}: WelcomeSectionProps) {
  return <>
      <WelcomeVideoSection applyUrl={applyUrl} />
      <RealIssueSection />
    </>;
}